(() => {
  'use strict';
  if (window.NorthyardImages) return;
  const config = document.querySelector('[data-image-feedback-config]')?.dataset || {};
  const labels = { loading: config.loading, slow: config.slow, error: config.error, retry: config.retry };
  const swatches = '.product-card__swatch,.qv-value.is-color,.product-option__swatch,.product-mobile-bar__swatch';
  const records = new Map(), hosts = new Map(), waits = new Map();
  let timer = 0, frame = 0;
  const isImage = (el) => el instanceof HTMLImageElement;
  const source = (el) => isImage(el)
    ? [el.getAttribute('src'), el.getAttribute('srcset'), el.dataset.src, el.dataset.srcset, el.dataset.nyState,
      ...Array.from(el.closest('picture')?.querySelectorAll('source') || [], (node) => node.srcset)].join('|')
    : (el.style.getPropertyValue('--swatch-image') || el.style.getPropertyValue('--qv-swatch-image')).match(/url\(['"]?(.*?)['"]?\)/)?.[1] || '';
  const deferred = (el) => isImage(el) && (['preview', 'loading', 'error'].includes(el.dataset.nyState) || (el.dataset.src || el.dataset.srcset)
    && (!el.getAttribute('src') || /^data:/.test(el.getAttribute('src'))));
  const ready = (r) => !r.manual && r.image?.complete && r.image.naturalWidth > 0 && !deferred(r.el);
  const visible = (r) => r.intersecting && !document.hidden && r.el.isConnected
    && !r.el.closest('[hidden],dialog:not([open]),[data-qv-media-slide][aria-hidden="true"]')
    && getComputedStyle(r.el).visibility === 'visible';

  function clearOverlay(r) {
    r.overlay?.remove();
    if (r.host && hosts.has(r.host)) {
      const count = hosts.get(r.host) - 1;
      if (count) hosts.set(r.host, count);
      else { hosts.delete(r.host); r.host.classList.remove('ny-image-feedback-host'); }
    }
    r.overlay = r.host = null;
  }
  function settle(r) {
    r.state = 'ready'; r.hadImage = true;
    clearOverlay(r); intersection.unobserve(r.el); resize.unobserve(r.el);
  }
  function remove(r) {
    clearOverlay(r); intersection.unobserve(r.el); resize.unobserve(r.el);
    if (r.load) { r.el.removeEventListener('load', r.load); r.el.removeEventListener('error', r.error); }
    records.delete(r.el); waits.delete(r.el);
  }
  function place(r) {
    if (!r.overlay || !r.host) return;
    const box = r.el.getBoundingClientRect(), host = r.host.getBoundingClientRect();
    if (!box.width || !box.height) { r.overlay.hidden = true; return; }
    const sx = host.width / (r.host.offsetWidth || host.width) || 1;
    const sy = host.height / (r.host.offsetHeight || host.height) || 1;
    let width = box.width / sx, height = box.height / sy;
    let left = (box.left - host.left) / sx + r.host.scrollLeft - r.host.clientLeft;
    let top = (box.top - host.top) / sy + r.host.scrollTop - r.host.clientTop;
    if (!isImage(r.el) && !r.manual) {
      const pseudo = r.el.matches('.product-card__swatch,.qv-value.is-color');
      const size = pseudo ? parseFloat(getComputedStyle(r.el, '::before').width) : Math.min(width, height) - 6;
      left += (width - size) / 2; top += (height - size) / 2;
      width = height = size;
    }
    r.compact = width < 120 || height < 90;
    r.overlay.classList.toggle('is-compact', r.compact);
    Object.assign(r.overlay.style, { left: left + 'px', top: top + 'px', width: width + 'px', height: height + 'px',
      borderRadius: !isImage(r.el) && !r.manual ? '50%' : getComputedStyle(r.el).borderRadius });
  }
  function build(r) {
    if (r.overlay || !r.el.isConnected) return;
    let host = r.manual ? r.el : r.el.parentElement;
    if (host?.tagName === 'PICTURE') host = host.parentElement;
    const interactive = r.el.closest('a,button,label,summary');
    if (interactive?.contains(host)) host = interactive.parentElement;
    while (host && getComputedStyle(host).display === 'contents') host = host.parentElement;
    // Do not create a new containing block that moves an absolutely positioned source image.
    if (!r.manual && getComputedStyle(r.el).position === 'absolute' && getComputedStyle(host).position === 'static') {
      host = r.el.offsetParent || host;
    }
    if (!host || !host.contains(r.el)) return;
    if (getComputedStyle(host).position === 'static' || hosts.has(host)) {
      hosts.set(host, (hosts.get(host) || 0) + 1); host.classList.add('ny-image-feedback-host');
    }
    r.host = host;
    const overlay = document.createElement('span');
    overlay.className = 'ny-image-feedback' + (r.hadImage || r.manual ? ' is-refresh' : '');
    const status = document.createElement('span');
    status.className = 'ny-image-feedback__status'; status.setAttribute('role', 'status');
    const message = document.createElement('span');
    const retry = document.createElement('button');
    retry.type = 'button'; retry.className = 'ny-image-feedback__retry'; retry.textContent = labels.retry;
    retry.hidden = true;
    retry.addEventListener('click', (event) => {
      event.preventDefault(); event.stopPropagation();
      // A progressive retry removes this overlay synchronously. Move keyboard
      // focus to stable nearby content BEFORE handing off to that loader.
      if (document.activeElement === retry) {
        const context = r.el.closest('article,[data-product-card],.product-media__item,.qv-gallery__slide');
        const focusTarget = context?.querySelector('h2,h3,h4')
          || context?.querySelector('button[data-product-media-open],a[href]') || r.el.closest('a,button')
          || context || r.el.parentElement.closest('div,section,li,figure,main') || r.el.parentElement;
        if (!focusTarget.matches('a[href],button,input,[tabindex]')) {
          focusTarget.setAttribute('tabindex', '-1');
          focusTarget.addEventListener('blur', () => focusTarget.removeAttribute('tabindex'), { once: true });
        }
        focusTarget.focus({ preventScroll: true });
      }
      if (r.manual && r.onRetry && (r.state === 'error' || r.elapsed >= 15000)) {
        r.onRetry(); return;
      }
      if (isImage(r.el) && window.NorthyardProgressive?.retry(r.el)) return;
      if (r.state !== 'error' || !isImage(r.el)) return;
      // A failed native image needs a fresh element, preserving the responsive source and original URL.
      const old = r.el, image = old.cloneNode(true);
      remove(r); old.replaceWith(image); track(image);
    });
    status.append(message, retry); overlay.append(status); host.append(overlay);
    r.overlay = overlay; r.message = message; r.retry = retry;
    place(r);
  }
  function paint(r) {
    if (r.state === 'ready') return;
    if (ready(r)) { settle(r); return; }
    if (!visible(r)) {
      if (r.overlay) { r.overlay.hidden = true; r.overlay.classList.remove('is-pulsing'); }
      r.last = 0; return;
    }
    build(r);
    if (!r.overlay) return;
    r.overlay.hidden = false;
    const preview = !!window.NorthyardProgressive?.hasPreview(r.el);
    r.overlay.classList.toggle('is-refresh', r.hadImage || r.manual || preview);
    // A same-source preview is usable content. A manual color wait instead
    // retains the OLD color, so it must keep its explicit selection message.
    r.overlay.classList.toggle('is-quiet', !r.manual);
    const now = performance.now();
    if (r.last) r.elapsed += Math.max(0, now - r.last);
    r.last = now;
    const failed = r.state === 'error', slow = r.elapsed >= (r.manual ? 15000 : 8000), retryable = r.elapsed >= 15000;
    r.overlay.classList.toggle('is-pulsing', !failed && !slow && !preview);
    const showMessage = !r.compact && (failed || r.elapsed >= (r.manual ? 1500 : 8000));
    r.overlay.classList.toggle('has-message', showMessage);
    r.overlay.classList.toggle('is-error', failed);
    const text = { ...labels, ...r.labels };
    const message = failed ? text.error : slow ? text.slow : text.loading;
    if (r.message.textContent !== (showMessage ? message : '')) r.message.textContent = showMessage ? message : '';
    r.retry.hidden = r.compact || (r.manual ? !(r.onRetry && (failed || retryable)) : !((failed || (retryable && r.el.hasAttribute('data-ny-image'))) && isImage(r.el)));
    r.overlay.setAttribute('aria-hidden', showMessage ? 'false' : 'true');
  }
  function tick() {
    timer = 0; let active = false;
    for (const r of [...records.values(), ...waits.values()]) {
      if (!r.el.isConnected) { remove(r); continue; }
      if (r.state === 'ready') continue;
      paint(r);
      if (visible(r) && r.state === 'loading' && r.elapsed < 15000) active = true;
    }
    if (active) timer = setTimeout(tick, 250);
  }
  function wake() { if (!timer) timer = setTimeout(tick, 0); }
  const intersection = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const r = records.get(entry.target) || waits.get(entry.target);
      if (!r) continue;
      r.intersecting = entry.isIntersecting; r.last = 0;
      if (r.intersecting && !isImage(r.el) && !r.manual && !r.image) loadSwatch(r);
      place(r); paint(r);
    }
    wake();
  });
  const resize = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const r = records.get(entry.target) || waits.get(entry.target);
      if (r) { place(r); paint(r); }
    }
    wake();
  });
  function loadSwatch(r) {
    const image = new Image(), key = r.key;
    r.image = image;
    const finish = () => {
      if (records.get(r.el) !== r || r.key !== key) return;
      if (ready(r)) settle(r); else { r.state = 'error'; paint(r); }
    };
    image.onload = finish; image.onerror = finish;
    // Match the already-visible CSS background URL; the browser coalesces/caches this request.
    image.src = key;
    if (image.complete) finish();
  }
  function reset(r) {
    r.key = source(r.el); r.elapsed = 0; r.last = 0; r.state = 'loading';
    clearOverlay(r);
    r.image = isImage(r.el) ? r.el : null;
    if (ready(r)) { settle(r); return; }
    if (r.el.dataset.nyState === 'error' || (isImage(r.el) && r.el.complete && r.el.getAttribute('src') && !deferred(r.el))) r.state = 'error';
    intersection.observe(r.el); resize.observe(r.el);
    if (r.intersecting && !isImage(r.el)) loadSwatch(r);
    paint(r); wake();
  }
  function track(el) {
    if (!(el instanceof HTMLElement) || el.closest('[data-search-image],.ny-image-feedback,[data-image-feedback-skip]')) return;
    if (!isImage(el) && !el.matches(swatches)) return;
    const key = source(el);
    if (!key.replaceAll('|', '') || (isImage(el) && el.width <= 1 && el.height <= 1 && el.hasAttribute('width'))) {
      if (records.has(el)) remove(records.get(el));
      return;
    }
    const existing = records.get(el);
    if (existing) { if (key !== existing.key) reset(existing); return; }
    const r = { el, state: 'loading', key, elapsed: 0, last: 0, intersecting: false, hadImage: false };
    records.set(el, r);
    if (isImage(el)) {
      r.load = () => { if (ready(r)) settle(r); else { r.state = 'loading'; reset(r); } };
      r.error = () => { if (!ready(r) && (!el.hasAttribute('data-ny-image') || ['error', 'native'].includes(el.dataset.nyState))) { r.state = 'error'; paint(r); } };
      el.addEventListener('load', r.load); el.addEventListener('error', r.error);
    }
    reset(r);
  }
  function scan(root) {
    if (!(root instanceof Element) || root.closest('.ny-image-feedback')) return;
    track(root); root.querySelectorAll('img,' + swatches).forEach(track);
  }
  document.addEventListener('northyard:image-state', event => {
    track(event.target);
    const r = records.get(event.target);
    if (r) paint(r);
  });
  const mutations = new MutationObserver((changes) => {
    let removed = false, changed = false;
    for (const change of changes) {
      if (change.target.closest?.('.ny-image-feedback')) continue;
      if (change.type === 'childList') {
        const added = [...change.addedNodes].filter((node) => !node.classList?.contains('ny-image-feedback'));
        added.forEach(scan);
        const detached = [...change.removedNodes].some((node) => !node.classList?.contains('ny-image-feedback'));
        removed ||= detached; changed ||= added.length > 0 || detached;
      } else if (['aria-hidden', 'hidden', 'open', 'class'].includes(change.attributeName)) {
        for (const r of [...records.values(), ...waits.values()]) {
          if (r.state !== 'ready' && change.target.contains(r.el)) { place(r); paint(r); }
        }
        changed = true;
      } else if (change.target.tagName === 'SOURCE') {
        change.target.closest('picture')?.querySelectorAll('img').forEach(track);
        changed = true;
      } else { track(change.target); changed = true; }
    }
    if (removed) for (const r of [...records.values(), ...waits.values()]) if (!r.el.isConnected) remove(r);
    if (changed) wake();
  });
  function reposition() {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      for (const r of [...records.values(), ...waits.values()]) if (r.overlay) place(r);
    });
  }
  document.addEventListener('visibilitychange', () => {
    clearTimeout(timer); timer = 0;
    for (const r of [...records.values(), ...waits.values()]) r.last = 0;
    tick();
  });
  document.addEventListener('scroll', reposition, { capture: true, passive: true });
  // CSS-only desktop menus can change visibility without changing their layout or attributes.
  ['pointerover', 'focusin'].forEach((name) => document.addEventListener(name, (event) => {
    if (event.target.closest?.('header,nav,[data-product-card]')) wake();
  }, { passive: true }));
  window.addEventListener('resize', () => {
    // Native picture/srcset may select another source without changing DOM attributes.
    for (const r of records.values()) if (isImage(r.el) && r.state === 'ready' && !ready(r)) reset(r);
    reposition();
  }, { passive: true });
  document.addEventListener('shopify:section:unload', (event) => {
    for (const r of [...records.values(), ...waits.values()]) if (event.target.contains(r.el)) remove(r);
  });
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  window.NorthyardImages = {
    scan,
    wait(el, options = {}) {
      if (!el || !el.isConnected) return () => {};
      const old = waits.get(el); if (old) remove(old);
      const r = { el, manual: true, state: 'loading', elapsed: 0, last: 0, intersecting: false,
        labels: options.labels, onRetry: options.onRetry };
      waits.set(el, r); intersection.observe(el); resize.observe(el); wake();
      const finish = () => { if (waits.get(el) === r) remove(r); };
      finish.slow = () => { if (waits.get(el) === r) { r.elapsed = 15000; paint(r); wake(); } };
      finish.error = () => { if (waits.get(el) === r) { r.state = 'error'; paint(r); wake(); } };
      return finish;
    }
  };
  scan(document.body);
  mutations.observe(document.body, { subtree: true, childList: true, attributes: true,
    attributeFilter: ['src', 'srcset', 'data-src', 'data-srcset', 'style', 'aria-hidden', 'hidden', 'open', 'class'] });
})();
